import { getDownloadURL, ref } from 'firebase/storage';
import { storage } from '../firebase/app';
import { groupByXYDifference } from '../utils/hotspot-group-by-difference';
import { urlParam } from '../utils/url-param';
import { reorderList } from '../utils/reorder-list';
import {
  CarViewType,
  ImageData,
  Model,
  ModelMainSpin,
  Report,
} from '../types/common-types';
import { handleDownloadFromGcs } from './gcs';
import { sortImagesByPath } from '../utils/sort-image-by-path';

function filterModelMainSpinByType(
  modelMainSpins: ModelMainSpin[],
  modelType: CarViewType,
) {
  return modelMainSpins.filter((model) => model.type === modelType);
}

async function createExteriorModel(
  userId: string,
  modelId: string,
  exterior: ModelMainSpin[],
) {
  const exteriorImagesPath: string[] = [];
  const url = `users/${userId}/models/${modelId}/`;

  return await new Promise((resolve) => {
    exterior?.map((item) => {
      const filename: string = item?.filename;
      const storageRef = url + filename;

      getDownloadURL(ref(storage, storageRef))
        .then((url) => {
          exteriorImagesPath.push(url);
          if (exteriorImagesPath.length === exterior.length) {
            resolve(sortImagesByPath(exterior, exteriorImagesPath));
            return;
          }
        })
        .catch((e) => console.log(e));
    });
  });
}

function createInteriorSections(interiorImages: ImageData[]) {
  let centerConsoleUrl: ImageData | null = null;
  let backSeatUrl: ImageData | null = null;

  interiorImages?.forEach((image: any) => {
    if (
      image.filename.includes('front') ||
      image.filename.includes('centerConsole')
    ) {
      centerConsoleUrl = image.url;
    }

    if (
      image.filename.includes('back') ||
      image.filename.includes('backSeat')
    ) {
      backSeatUrl = image.url;
    }
  });
  return { centerConsoleUrl, backSeatUrl };
}

async function createInteriorModel(
  modelMainSpins: ModelMainSpin[],
  userId: string,
  modelId: string,
) {
  modelMainSpins.forEach((item) => {
    if (item?.reports) {
      item.reports = groupByXYDifference(item?.reports, 0.0, 0.0);
    }
  });

  const interiorImagesPath: ImageData[] = [];

  return await new Promise((resolve) => {
    const interior = filterModelMainSpinByType(modelMainSpins, 'interior');

    const isInteriorEmpty = interior.length === 0;

    if (isInteriorEmpty) {
      return resolve(null);
    }

    interior?.forEach((model) => {
      const filename = model?.filename;

      handleDownloadFromGcs(userId, modelId, filename)
        .then((url: any) => {
          const imageData = {
            filename: filename,
            url: url,
          };
          interiorImagesPath.push(imageData);
          if (interiorImagesPath.length === interior.length) {
            const interiorImages = createInteriorSections(interiorImagesPath);
            return resolve(interiorImages);
          }
        })
        .catch((e) => console.log(e));
    });
  });
}

function filterModelMainSpinByStatus(modelMainSpins: ModelMainSpin[]) {
  return modelMainSpins.filter((model: ModelMainSpin) => model.status === true);
}

function reorderModelMainSpins(modelMainSpins: ModelMainSpin[]) {
  const order = Number(urlParam('order'));

  if (order) {
    modelMainSpins = reorderList(Number(order), modelMainSpins);
  }

  return modelMainSpins;
}

function prepareModelMainSpins(modelMainSpins: ModelMainSpin[]) {
  const filteredModelMainSpinByStatus =
    filterModelMainSpinByStatus(modelMainSpins);

  const reorderedModelMainSpins = reorderModelMainSpins(
    filteredModelMainSpinByStatus,
  );

  return reorderedModelMainSpins;
}

function findEngineVideoFilenameInHotspotList(modelMainSpins: ModelMainSpin[]) {
  let engineVideoFilename: null | string = null;

  let reportList: Report[] = [];

  modelMainSpins?.forEach((model: ModelMainSpin) => {
    if (model?.reports) {
      reportList.push(...model.reports);
    }
  });

  reportList.forEach((report: Report) => {
    if (report.contentType === 'video' && engineVideoFilename === null) {
      engineVideoFilename = report.filename;
      return;
    }
  });

  return engineVideoFilename;
}

const prepareAutoHotspot = (autoHotspotData: any) => {
  const updatedHotspotData = autoHotspotData.map((hotspot: any) => {
    let featuresListUnrepeated: string[] = [];
    return {
      ...hotspot,
      featuresList: hotspot.features
        .map((item: any) => {
          if (!featuresListUnrepeated.includes(item.type)) {
            featuresListUnrepeated.push(item.type);
            return item.type;
          }
        })
        .filter(Boolean),
      positions: hotspot.positions.map((pos: any) => {
        const [x, y] = pos.pos.split(',').map(Number);
        return {
          frameOrder: pos.frameOrder,
          x: x * 100,
          y: y * 100,
        };
      }),
    };
  });

  return updatedHotspotData;
};

export async function getModelProcess(model: Model) {
  const userId = model?.user_id;
  const modelId = model?.model_id;
  const modelMainSpins = prepareModelMainSpins(model?.modelMainSpins);
  const exterior = filterModelMainSpinByType(modelMainSpins, 'exterior');
  const engineVideoFilename =
    findEngineVideoFilenameInHotspotList(modelMainSpins);

  const autoHotspots = prepareAutoHotspot(model?.autoHotspots);

  // const autoHotspots = autoHotspotData;

  // const positions = autoHotspot[0]['positions'].map((item: any) => {
  //   return {
  //     frameOrder: Number(item.frameOrder),
  //     x: Number(item.pos.split(',')[0]),
  //     y: Number(item.pos.split(',')[1]),
  //   };
  // });
  // const autoHotspot = model?.autoHotspot;

  return Promise.all([
    userId,
    modelId,
    engineVideoFilename,
    modelMainSpins,
    createExteriorModel(userId, modelId, exterior),
    createInteriorModel(modelMainSpins, userId, modelId),
    autoHotspots,
  ]).then(
    ([
      userId,
      modelId,
      engineVideoFilename,
      modelMainSpin,
      exteriorModel,
      interiorModel,
      autoHotspots,
    ]) => ({
      userId,
      modelId,
      engineVideoFilename,
      modelMainSpin,
      exteriorModel,
      interiorModel,
      autoHotspots,
    }),
  );
}
